import { GET_SEARCH_IMAGES_SUCCESS, GetSearchImagesSuccessAction } from 'js/actionCreators/ImageDrawerActions';
import {
  CLEAR_SEARCH_INPUT,
  RESET_SEARCH,
  ResetSearchAction,
  ClearSearchInputAction,
  SEARCH_IMAGES,
  SearchImagesAction,
  SET_SEARCH_INPUT,
  SET_SELECTED_CATEGORY,
  SetSearchInputAction,
  SetSelectedCategoryAction,
  ToggleSearchFilterAction,
  TOGGLE_SEARCH_FILTER,
  CLEAR_ALL_FILTERS,
  ClearAllFiltersAction,
  ACTIVATE_VISUAL_STYLE_SEARCH,
  ActivateVisualStyleSearchAction,
  NAVIGATE_TO_CHARACTERS,
  NavigateToCharactersAction,
  NAVIGATE_BACK_FROM_CHARACTERS,
  NavigateBackFromCharactersAction,
  SELECT_CHARACTER,
  SelectCharacterAction,
  LOAD_VISUAL_STYLES,
  LOAD_VISUAL_STYLES_SUCCESS,
  LOAD_VISUAL_STYLES_FAILURE,
  LoadVisualStylesAction,
  LoadVisualStylesSuccessAction,
  LoadVisualStylesFailureAction,
  VisualStyleItem
} from 'js/actionCreators/imageSearchActions';
import { EDITOR_CLOSED, EditorClosedAction } from 'js/actionCreators/editorActions';
import { FacetsList } from 'js/types';
import { SearchMode } from 'js/editor/ImageDrawer/imageSearchModeConfig';
import { CharacterStyle } from 'js/config/consts';

export const DEFAULT_STREAMLINE_SEARCH_TERM = 'people';

export enum SearchType {
  INPUT = 'search',
  CATEGORY = 'category'
}

export enum ImageLibraryStep {
  CATEGORIES,
  SEARCH_RESULT,
  CHARACTERS
}

export type FacetFilterConfig = {
  [facetId: string]: string[];
};

export interface ImageSearchState {
  searchInput: string;
  submittedSearchInput: string;
  isSearchInputSubmitted: boolean;
  searchType: SearchType;
  selectedCategoryId: string | null;
  displayedStep: ImageLibraryStep;
  facets: FacetsList;
  selectedFilters: FacetFilterConfig;
  visualStyleFilter: FacetFilterConfig;
  hideBackToCategories?: boolean;
  visualStyles: VisualStyleItem[];
  visualStylesStatus: 'idle' | 'loading' | 'success' | 'error';
  activeSearchMode: SearchMode;
  characterSeedTerms: string[];
  selectedCharacterName: string | null;
  selectedCharacterStyle: CharacterStyle | null;
}

const initialState: ImageSearchState = {
  searchInput: '',
  submittedSearchInput: '',
  isSearchInputSubmitted: false,
  searchType: SearchType.INPUT,
  selectedCategoryId: null,
  displayedStep: ImageLibraryStep.CATEGORIES,
  facets: [],
  selectedFilters: {} as FacetFilterConfig,
  visualStyleFilter: {} as FacetFilterConfig,
  hideBackToCategories: false,
  visualStyles: [],
  visualStylesStatus: 'idle',
  activeSearchMode: 'default',
  characterSeedTerms: [],
  selectedCharacterName: null,
  selectedCharacterStyle: null
};

type ActionType =
  | SetSearchInputAction
  | ClearSearchInputAction
  | SearchImagesAction
  | SetSelectedCategoryAction
  | ResetSearchAction
  | GetSearchImagesSuccessAction
  | ToggleSearchFilterAction
  | ClearAllFiltersAction
  | ActivateVisualStyleSearchAction
  | SelectCharacterAction
  | EditorClosedAction
  | LoadVisualStylesAction
  | LoadVisualStylesSuccessAction
  | LoadVisualStylesFailureAction
  | NavigateToCharactersAction
  | NavigateBackFromCharactersAction;

export default function imageSearchReducer(state = initialState, action: ActionType) {
  switch (action.type) {
    case SET_SEARCH_INPUT: {
      return {
        ...state,
        isSearchInputSubmitted: false,
        searchInput: action.searchInput
      };
    }

    case CLEAR_SEARCH_INPUT: {
      return {
        ...state,
        isSearchInputSubmitted: false,
        searchInput: ''
      };
    }

    case SEARCH_IMAGES: {
      return {
        ...state,
        submittedSearchInput: state.searchInput,
        isSearchInputSubmitted: true,
        searchType: SearchType.INPUT,
        selectedCategoryId: null,
        displayedStep: ImageLibraryStep.SEARCH_RESULT,
        selectedFilters: {} as FacetFilterConfig,
        facets: []
      };
    }

    case SET_SELECTED_CATEGORY: {
      return {
        ...state,
        isSearchInputSubmitted: false,
        selectedCategoryId: action.selectedCategoryId,
        searchType: SearchType.CATEGORY,
        displayedStep: ImageLibraryStep.SEARCH_RESULT,
        searchInput: '',
        submittedSearchInput: '',
        selectedFilters: {} as FacetFilterConfig,
        facets: []
      };
    }

    case EDITOR_CLOSED:
    case RESET_SEARCH: {
      return {
        ...state,
        isSearchInputSubmitted: false,
        selectedCategoryId: null,
        searchInput: '',
        submittedSearchInput: '',
        searchType: SearchType.INPUT,
        displayedStep: ImageLibraryStep.CATEGORIES,
        selectedTag: null,
        selectedFilters: {} as FacetFilterConfig,
        visualStyleFilter: {} as FacetFilterConfig,
        facets: [],
        hideBackToCategories: false,
        activeSearchMode: 'default' as SearchMode,
        characterSeedTerms: [],
        selectedCharacterName: null,
        selectedCharacterStyle: null
      };
    }

    case ACTIVATE_VISUAL_STYLE_SEARCH: {
      const isStreamline = action.facetId === 'streamline';
      const searchTerm = isStreamline ? state.searchInput || DEFAULT_STREAMLINE_SEARCH_TERM : state.searchInput;
      return {
        ...state,
        activeSearchMode: (isStreamline ? 'streamlineStyle' : 'otherVisualStyle') as SearchMode,
        searchInput: state.searchInput,
        submittedSearchInput: searchTerm,
        isSearchInputSubmitted: true,
        searchType: SearchType.INPUT,
        selectedCategoryId: null,
        displayedStep:
          state.displayedStep === ImageLibraryStep.CATEGORIES ? ImageLibraryStep.SEARCH_RESULT : state.displayedStep,
        visualStyleFilter: { [action.facetId]: [action.facetValue] },
        selectedFilters: {} as FacetFilterConfig,
        facets: [],
        hideBackToCategories: true
      };
    }

    case SELECT_CHARACTER: {
      return {
        ...state,
        isSearchInputSubmitted: false,
        activeSearchMode: 'character' as SearchMode,
        characterSeedTerms: ['Namedcharacter', action.characterName],
        selectedCharacterName: action.characterName,
        selectedCharacterStyle: action.characterStyle,
        searchInput: '',
        submittedSearchInput: '',
        searchType: SearchType.INPUT,
        selectedCategoryId: null,
        displayedStep: ImageLibraryStep.SEARCH_RESULT,
        selectedFilters: {} as FacetFilterConfig,
        facets: []
      };
    }

    case GET_SEARCH_IMAGES_SUCCESS: {
      if (action.imageSource !== 'videoscribe' || !action.facets) return state;

      if (!state.facets.length) {
        return {
          ...state,
          facets: action.facets || []
        };
      }

      const isPaginationLoad = action.pagination?.some(p => p.offset > 0);
      if (isPaginationLoad) return state;

      const newFacets = structuredClone(state.facets);

      newFacets.forEach(facet => {
        const incomingFacet = action.facets?.find(f => f.name === facet.name && f.source === facet.source);

        facet.values.forEach(facetValue => {
          facetValue.disabled = false;

          if (!incomingFacet) {
            facetValue.disabled = true;
            return;
          }

          const incomingFacetValue = incomingFacet.values.find(fv => fv.slug === facetValue.slug);

          if (!incomingFacetValue) {
            facetValue.disabled = true;
            return;
          }
          facetValue.count = incomingFacetValue.count;

          if (facetValue.count === 0) {
            facetValue.disabled = true;
          }
        });
      });

      return {
        ...state,
        facets: newFacets
      };
    }

    case TOGGLE_SEARCH_FILTER: {
      const { facetId, facetValue } = action;

      if (!state.selectedFilters[facetId]) {
        return {
          ...state,
          selectedFilters: {
            ...state.selectedFilters,
            [facetId]: [facetValue]
          }
        };
      } else {
        if (state.selectedFilters[facetId].includes(facetValue)) {
          const updatedValues = state.selectedFilters[facetId].filter(val => val !== facetValue);
          const updatedFilters = { ...state.selectedFilters };
          if (updatedValues.length === 0) {
            delete updatedFilters[facetId];
          } else {
            updatedFilters[facetId] = updatedValues;
          }
          return {
            ...state,
            selectedFilters: updatedFilters
          };
        } else {
          return {
            ...state,
            selectedFilters: {
              ...state.selectedFilters,
              [facetId]: state.selectedFilters[facetId].concat([facetValue])
            }
          };
        }
      }
    }

    case CLEAR_ALL_FILTERS: {
      return {
        ...state,
        selectedFilters: {}
      };
    }

    case NAVIGATE_TO_CHARACTERS: {
      return {
        ...state,
        displayedStep: ImageLibraryStep.CHARACTERS
      };
    }

    case NAVIGATE_BACK_FROM_CHARACTERS: {
      return {
        ...state,
        displayedStep: ImageLibraryStep.SEARCH_RESULT,
        activeSearchMode: 'otherVisualStyle' as SearchMode,
        characterSeedTerms: [],
        selectedCharacterName: null,
        selectedCharacterStyle: null,
        searchInput: '',
        submittedSearchInput: '',
        selectedFilters: {} as FacetFilterConfig,
        facets: []
      };
    }

    case LOAD_VISUAL_STYLES: {
      return { ...state, visualStylesStatus: 'loading' as const };
    }

    case LOAD_VISUAL_STYLES_SUCCESS: {
      return { ...state, visualStylesStatus: 'success' as const, visualStyles: action.styles };
    }

    case LOAD_VISUAL_STYLES_FAILURE: {
      return { ...state, visualStylesStatus: 'error' as const };
    }

    default:
      return state;
  }
}
